import React, { Fragment, type ReactNode, useCallback, useEffect, useRef } from 'react';

import { FabricChannel } from '@atlaskit/analytics-listeners';
import {
	type AnalyticsEventPayload,
	UIAnalyticsEvent,
	useAnalyticsEvents,
} from '@atlaskit/analytics-next';

import type { Edition, Product } from '../types';

import { useEditionAwareness } from './edition-awareness-context';

export const ANALYTICS_CHANNEL = FabricChannel.growth;

const getAttributesFromContexts = (context: unknown): Record<string, any> => {
	if (!context || !Array.isArray(context)) {
		return {};
	}

	return context.reduce((acc, x) => {
		return {
			...acc,
			...(x ?? {}),
		};
	}, {});
};

const defaultAttributes = {
	packageName: '@atlassian/edition-awareness',
	source: 'editionAwareness',
};

export function useErrorAnalytics({ product, edition }: { product: Product; edition: Edition }) {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const handleOnError = useCallback(
		(error: Error) => {
			const event = createAnalyticsEvent({
				eventType: 'operational',
				action: 'failed',
				actionSubject: '@atlassian/edition-awareness.edition-awareness',
				source: 'unknownErrorSource',
				attributes: {
					...defaultAttributes,
					product,
					edition,
					error: error.message,
				},
			});

			const contextAttributes = getAttributesFromContexts(event.context);

			event
				.update({
					attributes: {
						...event.payload.attributes,
						...contextAttributes,
					},
				})
				.fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, product, edition],
	);

	return handleOnError;
}

export function useAnalytics() {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const editionAwarenessContext = useEditionAwareness();

	const sendAnalyticsEvent = useCallback(
		(payload: AnalyticsEventPayload | UIAnalyticsEvent) => {
			if (payload instanceof UIAnalyticsEvent) {
				const contextAttributes = getAttributesFromContexts(payload.context);

				payload
					.update({
						...payload.payload,
						attributes: {
							...defaultAttributes,
							...editionAwarenessContext,
							...payload.payload.attributes,
							...contextAttributes,
						},
					})
					.fire(ANALYTICS_CHANNEL);
			} else {
				const event = createAnalyticsEvent({
					...payload,
					attributes: {
						...defaultAttributes,
						...payload.attributes,
						...editionAwarenessContext,
					},
				});

				const contextAttributes = getAttributesFromContexts(event.context);

				event
					.update({
						attributes: {
							...event.payload.attributes,
							...contextAttributes,
						},
					})
					.fire(ANALYTICS_CHANNEL);
			}
		},
		[createAnalyticsEvent, editionAwarenessContext],
	);

	return {
		sendAnalyticsEvent,
	};
}

/**
 * Wrap a component which will fire a screen event once when first rendered with the `name`
 */
export function ScreenEvent({ children, name }: { children: ReactNode; name: string }) {
	const { sendAnalyticsEvent } = useAnalytics();
	const hasSent = useRef(false);

	useEffect(() => {
		if (hasSent.current) {
			return;
		}
		sendAnalyticsEvent({
			eventType: 'screen',
			name,
		});
		hasSent.current = true;
	}, [sendAnalyticsEvent, name]);

	return <Fragment>{children}</Fragment>;
}
